/**
 * Bonus programs: reads (rules-gated) and admin callables.
 *
 *   tenants/{tid}.bonusConfig.enabled                 feature gate
 *   tenants/{tid}/bonusPrograms/{programId}           (+ configVersions/{quarter})
 *   tenants/{tid}/bonusEnrollments/{id}               who is scored, on which clients
 *   tenants/{tid}/bonusProgramSupport/{p}__{uid}__{ym}
 *   tenants/{tid}/bonusOverrides/{p}__{quarter}__{uid}
 *   tenants/{tid}/bonusQuarters/{p}__{quarter}
 *   tenants/{tid}/bonusScorecards/{p}__{quarter}__{uid}  (owner or admin)
 *   tenants/{tid}/clientKpiMonthly/{p}__{ym}             (KPI report, no employee data)
 *   tenants/{tid}/bonusEnrollmentList/{quarter}          (admin enrollment review)
 */
import {
  collection, doc, documentId, getDoc, getDocs, limit, orderBy, query, where,
} from 'firebase/firestore';
import { db, functions, httpsCallable } from '../firebase';

const tCol = (tid, name) => collection(db, 'tenants', tid, name);
const rows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

export async function isBonusEnabled(tenantId) {
  if (!tenantId) return false;
  const snap = await getDoc(doc(db, 'tenants', tenantId));
  return snap.exists() && snap.data()?.bonusConfig?.enabled === true;
}

export async function listMyScorecards(tenantId, uid) {
  const snap = await getDocs(query(tCol(tenantId, 'bonusScorecards'), where('uid', '==', uid)));
  return rows(snap).sort((a, b) => (a.quarter < b.quarter ? 1 : a.quarter > b.quarter ? -1 : 0));
}

export async function hasAnyScorecard(tenantId, uid) {
  const snap = await getDocs(query(tCol(tenantId, 'bonusScorecards'), where('uid', '==', uid), limit(1)));
  return !snap.empty;
}

export async function listScorecards(tenantId, programId, quarter) {
  const snap = await getDocs(query(
    tCol(tenantId, 'bonusScorecards'),
    where('programId', '==', programId),
    where('quarter', '==', quarter),
  ));
  return rows(snap).sort((a, b) => String(a.userName).localeCompare(String(b.userName)));
}

export async function listPrograms(tenantId) {
  const snap = await getDocs(tCol(tenantId, 'bonusPrograms'));
  const programs = rows(snap);
  await Promise.all(programs.map(async (p) => {
    const versions = await getDocs(collection(db, 'tenants', tenantId, 'bonusPrograms', p.id, 'configVersions'));
    p.versions = rows(versions).sort((a, b) => (a.effectiveQuarter < b.effectiveQuarter ? -1 : 1));
  }));
  return programs.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export async function listEnrollments(tenantId, programId) {
  const snap = await getDocs(query(tCol(tenantId, 'bonusEnrollments'), where('programId', '==', programId)));
  return rows(snap).sort((a, b) => String(a.userName).localeCompare(String(b.userName)) || (a.startYm < b.startYm ? -1 : 1));
}

export async function listProgramSupport(tenantId, programId) {
  const snap = await getDocs(query(tCol(tenantId, 'bonusProgramSupport'), where('programId', '==', programId)));
  return rows(snap);
}

export async function listOverrides(tenantId, programId, quarter) {
  const snap = await getDocs(query(
    tCol(tenantId, 'bonusOverrides'),
    where('programId', '==', programId),
    where('quarter', '==', quarter),
  ));
  return rows(snap);
}

export async function listQuarters(tenantId, programId) {
  const snap = await getDocs(query(tCol(tenantId, 'bonusQuarters'), where('programId', '==', programId)));
  return rows(snap).sort((a, b) => (a.quarter < b.quarter ? 1 : -1));
}

/** Audit entries with start <= at < end (end optional), newest first. */
export async function listAuditLogRange(tenantId, start, end = null) {
  const clauses = [where('at', '>=', start)];
  if (end) clauses.push(where('at', '<', end));
  const snap = await getDocs(query(tCol(tenantId, 'bonusAuditLog'), ...clauses, orderBy('at', 'desc')));
  return rows(snap);
}

export async function getOldestAuditEntry(tenantId) {
  const snap = await getDocs(query(tCol(tenantId, 'bonusAuditLog'), orderBy('at', 'asc'), limit(1)));
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function getPatientDetail(tenantId, programId, ym) {
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'bonusPatientDetail', `${programId}__${ym}`));
  return snap.exists() ? snap.data() : null;
}

export async function listClientKpiMonthly(tenantId, yms) {
  if (!yms?.length) return [];
  const out = [];
  for (let i = 0; i < yms.length; i += 10) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(tCol(tenantId, 'clientKpiMonthly'), where('ym', 'in', yms.slice(i, i + 10))));
    out.push(...rows(snap));
  }
  return out;
}

export async function getEnrollmentList(tenantId, quarter) {
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'bonusEnrollmentList', quarter));
  return snap.exists() ? snap.data() : null;
}

/** Current Outreach User per patient: Map<patientId, uid|''>. */
export async function getPatientOutreachUsers(tenantId, patientIds) {
  const out = new Map();
  const ids = [...new Set(patientIds)];
  for (let i = 0; i < ids.length; i += 30) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(tCol(tenantId, 'patients'), where(documentId(), 'in', ids.slice(i, i + 30))));
    snap.docs.forEach((d) => out.set(d.id, d.data().outreachUserUid || ''));
  }
  return out;
}

async function call(name, data, timeout) {
  const fn = httpsCallable(functions, name, timeout ? { timeout } : undefined);
  const res = await fn(data);
  return res.data;
}

const LONG_MS = 540000;

export const saveProgram = (data) => call('bonusSaveProgram', data);
export const saveConfigVersion = (data) => call('bonusSaveConfigVersion', data);
export const saveEnrollment = (data) => call('bonusSaveEnrollment', data);
export const deleteEnrollment = (enrollmentId) => call('bonusDeleteEnrollment', { enrollmentId });
export const setProgramSupport = (data) => call('bonusSetProgramSupport', data);
export const setOverride = (data) => call('bonusSetOverride', data);
export const setQuarterInfo = (data) => call('bonusSetQuarterInfo', data);
export const setOutreachUsers = (assignments) => call('bonusSetOutreachUsers', { assignments });
export const recompute = (data) => call('bonusRecompute', data, LONG_MS);
export const setTenantBonusEnabled = (data) => call('bonusSetTenantEnabled', data);
